import * as React from 'react';
import styles from './Button.scss';
import classNames from 'classnames';
import SmallPreloader from '~/components/SmallPreloader/SmallPreloader';
import AnimationDecorator from '~/decorators/AnimationDecorator/AnimationDecorator';
import { playButtonClickSound, playButtonDialogClickSound } from '~/api/WoWsClient';
import { IButtonSoundset } from '~/types/soundsets';

export interface IButton {
    label: string | React.ReactChild | any;
    onClick?: (event?: React.MouseEvent) => void;
    onFallback?: (event?: React.MouseEvent) => void;
    disabled?: boolean;
    className?: string | string[];
    isFull?: boolean;
    isFree?: boolean;
    isWarn?: boolean;
    id?: string;
    autoFocus?: boolean;
    onMouseMove?: (event: React.MouseEvent) => void;
    isFetching?: boolean;
    linkTarget?: boolean;
    isSilent?: boolean;
    soundset?: IButtonSoundset;
}

const soundsetsMap: Record<IButtonSoundset, (() => void) | null> = {
    [IButtonSoundset.MUTED]: null,
    [IButtonSoundset.DEFAULT]: () => playButtonClickSound(),
    [IButtonSoundset.POPUP]: () => playButtonDialogClickSound(),
};

const playSoundset = (soundsetName: IButtonSoundset) => {
    soundsetsMap[soundsetName || IButtonSoundset.DEFAULT]?.();
};

const OrangeButton = function (props: IButton) {
    const isDisabled = props.disabled || props.isFetching;
    const buttonClassNames = classNames(
        styles.button,
        styles.orangeButton,
        {
            [styles.disabled]: isDisabled,
            [styles.isFull]: props.isFull,
            [styles.isFree]: props.isFree,
            [styles.isFetching]: props.isFetching,
        },
        props.className,
        'armory-decorator__animation',
    );

    const onClick = (event: React.MouseEvent) => {
        event.preventDefault();
        playSoundset(props.soundset);
        props.onClick && props.onClick(event);
    };

    const onFallback = () => {
        props.onFallback?.();
    };

    const onMouseMove = (event: React.MouseEvent) => {
        props.onMouseMove?.(event);
    };

    return (
        <button tabIndex={-1} className={buttonClassNames} onMouseMove={onMouseMove} onClick={isDisabled ? onFallback : onClick} autoFocus={props.autoFocus}>
            <AnimationDecorator mainColor={props.isFree ? '#E13C3C' : '#b23d03'} isDisabled={isDisabled}>
                {props.isFetching && <SmallPreloader className={styles.fetching} />}
                <span className={styles.label}>{props.label}</span>
            </AnimationDecorator>
        </button>
    );
};

const SpecialOrangeButton = function (props: IButton) {
    const isDisabled = props.disabled || props.isFetching;
    const buttonClassNames = classNames(
        styles.button,
        styles.specialOrangeButton,
        {
            [styles.disabled]: isDisabled,
            [styles.isFull]: props.isFull,
            [styles.isFetching]: props.isFetching,
        },
        props.className,
    );

    const onClick = (event: React.MouseEvent) => {
        event.preventDefault();
        playSoundset(props.soundset);
        props.onClick && props.onClick(event);
    };

    const onFallback = () => {
        props.onFallback?.();
    };

    const onMouseMove = (event: React.MouseEvent) => {
        props.onMouseMove?.(event);
    };

    return (
        <button tabIndex={-1} className={buttonClassNames} onMouseMove={onMouseMove} onClick={isDisabled ? onFallback : onClick} autoFocus={props.autoFocus}>
            {props.isFetching && <SmallPreloader className={styles.fetching} />}
            <span className={styles.label}>{props.label}</span>
        </button>
    );
};

const Button = function (props: IButton) {
    const isDisabled = props.disabled || props.isFetching;
    const buttonClassNames = classNames(
        styles.button,
        {
            [styles.disabled]: isDisabled,
            [styles.isFull]: props.isFull,
            [styles.isFetching]: props.isFetching,
        },
        props.className,
    );

    const onClick = (event: React.MouseEvent) => {
        event.preventDefault();
        playSoundset(props.soundset);
        props.onClick && props.onClick(event);
    };

    const onFallback = () => {
        props.onFallback?.();
    };

    return (
        <button className={buttonClassNames} onClick={isDisabled ? onFallback : onClick} autoFocus={props.autoFocus} id={props.id}>
            {props.isFetching && <SmallPreloader className={styles.fetching} />}
            <span className={styles.label}>{props.label}</span>
            {props.linkTarget && <div className={styles.arrowTarget}></div>}
        </button>
    );
};

const BlueButton = function (props: IButton) {
    const buttonClassNames = classNames(
        styles.button,
        styles.blueButton,
        {
            [styles.disabled]: props.disabled,
            [styles.isFull]: props.isFull,
            [styles.isFetching]: props.isFetching,
        },
        props.className,
        'armory-decorator__animation',
    );

    const onClick = (event: React.MouseEvent) => {
        event.preventDefault();
        playSoundset(props.soundset);
        props.onClick && props.onClick(event);
    };

    const onFallback = () => {
        props.onFallback?.();
    };

    return (
        <button className={buttonClassNames} onClick={props.disabled ? onFallback : onClick} autoFocus={props.autoFocus} id={props.id}>
            <AnimationDecorator mainColor={'#4EBCEB'} isDisabled={props.disabled || props.isFetching}>
                {props.isFetching && <SmallPreloader className={styles.fetching} />}
                <span className={styles.label}>{props.label}</span>
            </AnimationDecorator>
        </button>
    );
};

const AuctionOrangeButton = function (props: IButton) {
    const buttonClassNames = classNames(
        styles.button,
        styles.orangeButton,
        {
            [styles.isFull]: props.isFull,
            [styles.isFree]: props.isFree,
            [styles.isFetching]: props.isFetching,
            [styles.disabled]: props.disabled,
        },
        props.className,
    );

    const onClick = (event: React.MouseEvent) => {
        playSoundset(props.soundset);
        props.onClick && props.onClick(event);
    };

    const onMouseMove = (event: React.MouseEvent) => {
        props.onMouseMove?.(event);
    };

    const onFallback = () => {
        props.onFallback?.();
    };

    return (
        <button tabIndex={-1} className={buttonClassNames} onMouseMove={onMouseMove} onClick={props.disabled ? onFallback : onClick} autoFocus={props.autoFocus}>
            <span className={styles.label}>{props.label}</span>
        </button>
    );
};

export { OrangeButton, Button, AuctionOrangeButton, BlueButton, SpecialOrangeButton };
